<script lang="ts">
  import { createContext } from 'reka-ui'

  function defineTenantUserAccessRolesLayout() {
    const selectedDatabase = ref<Database['id'] | undefined>()

    return {
      selectedDatabase
    }
  }

  const [useTenantUserAccessRolesLayout, provideTenantUserAccessRolesLayout] =
    createContext<ReturnType<typeof defineTenantUserAccessRolesLayout>>(
      'tenant-user-access-roles-layout'
    )

  export { useTenantUserAccessRolesLayout }
</script>

<script setup lang="ts">
  import { TenantUserAccessRolesProvider } from '../providers'
  import { GsCloudBackendProvider } from '@gat-ship/http-client'
  import { useUser } from '@gat-ship/user'
  import { type Database } from '@gat-ship/types'
  import { computed, ref } from 'vue'
  import DatabaseSelect from '../inputs/DatabaseSelect.vue'
  import { AgencyStructureProvider } from '@gat-ship/agency'
  import { AccessRolesCollectionProvider } from '@gat-ship/access-control'
  import { useTenantOwnedDatabases } from '../providers'
  import { useTenantUserDatabaseAccessGrants } from '../providers'
  import { useTenantUserDetailContext } from '../../composables/useTenantUserDetail'
  import { useDatabaseAccessUtils } from '../../composables/useDatabaseAccessUtils'
  import { SectionHeader } from '@gat-ship/vue-components'

  const props = defineProps<{
    id?: string
  }>()

  const userService = useUser()

  const tenantOwnedDatabasesDataSource = useTenantOwnedDatabases()
  const { databaseAccessGrants } = useTenantUserDatabaseAccessGrants()
  const { DatabaseAccessUtils } = useDatabaseAccessUtils()
  const tenantUserProvider = useTenantUserDetailContext()!
  const tenantUser = computed(() => {
    if (!props.id) {
      return undefined
    }
    return tenantUserProvider.tenantUser.value
  })

  const layoutContext = defineTenantUserAccessRolesLayout()

  const { selectedDatabase } = provideTenantUserAccessRolesLayout(layoutContext)

  const accessibleDatabases = computed((): Database[] => {
    const userId = props.id
    if (!userId) {
      return []
    }
    return DatabaseAccessUtils.buildAccessibleDatabases(
      databaseAccessGrants.value,
      tenantOwnedDatabasesDataSource.getDatabaseAccessItems(userId),
      userService.databases.value.current
    )
  })

  const cardProps = computed(() => {
    if (accessibleDatabases.value.length === 0) {
      return {
        cardTitle: 'Access Roles',
        cardDescription: 'This user does not have access to any databases.'
      }
    }
    if (tenantUser.value?.isTenantAdmin) {
      return {
        cardTitle: 'Access Roles',
        cardDescription: 'A tenant admin have all permissions to all agencies.'
      }
    }
    return {
      cardTitle: 'Access Roles',
      cardDescription:
        'The roles assigned to this user that define their permissions.'
    }
  })

  function selectDatabase(databases: Database[]) {
    return DatabaseAccessUtils.selectDefaultDatabaseId(
      databases,
      userService.databases.value.current?.id
    )
  }
</script>
<template>
  <GsCloudBackendProvider :database-id="selectedDatabase">
    <AgencyStructureProvider>
      <AccessRolesCollectionProvider>
        <TenantUserAccessRolesProvider
          v-if="props.id"
          :database-id="selectedDatabase"
          :user-id="props.id"
        >
          <SectionHeader
            :title="cardProps.cardTitle"
            :description="cardProps.cardDescription"
          >
            <template #actions>
              <div class="ml-auto flex w-[200px]">
                <DatabaseSelect
                  v-if="
                    !tenantUser?.isTenantAdmin && accessibleDatabases.length > 0
                  "
                  v-model:model-value="selectedDatabase"
                  class="w-full"
                  :items="accessibleDatabases"
                  required
                  @vue:mounted="
                    () =>
                      (selectedDatabase = selectDatabase(accessibleDatabases))
                  "
                />
              </div>
            </template>
          </SectionHeader>
          <slot><router-view /></slot>
        </TenantUserAccessRolesProvider>
      </AccessRolesCollectionProvider>
    </AgencyStructureProvider>
  </GsCloudBackendProvider>
</template>
